import React, { useState } from 'react'
import { Button, Form } from 'react-bootstrap'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import * as Yup from 'yup'

const validationSchema = Yup.object({
    username: Yup.string().required(),
    password: Yup.string().required(),
    rememberMe: Yup.boolean(),
})

interface IAppLoginProps {
    canBeRestored?: boolean
    canBeRegistered?: boolean

    signInFunc(username: string, password: string, rememberMe?: boolean): void
}

interface IFormErrors {
    username: Yup.ValidationError | null
    password: Yup.ValidationError | null
}

export const AppLoginPage: React.FC<IAppLoginProps> = React.memo(({ canBeRestored, signInFunc }: IAppLoginProps) => {
    const intl = useIntl()
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const initialFormErrorsState: IFormErrors = { username: null, password: null }
    const [formErrors, setFormErrors] = useState(initialFormErrorsState)

    const usernamePlaceholder = intl.formatMessage({
        id: 'login.form.username',
    })
    const passwordPlaceholder = intl.formatMessage({
        id: 'login.form.password',
    })

    const restoreLink = (
        <p className="my-0">
            <Link to="/restore">
                <FormattedMessage id="login.forgot.password" />
            </Link>
        </p>
    )

    const handleSetUsername = (event: any) => {
        const { value } = event.target
        setUsername(value)

        try {
            (Yup.reach(validationSchema, 'username') as Yup.StringSchema).validateSync(value)
            const formErrorsNew = { ...formErrors }
            formErrorsNew.username = null
            setFormErrors(formErrorsNew)
        } catch (err) {
            const formErrorsNew = { ...formErrors }
            formErrorsNew.username = err
            setFormErrors(formErrorsNew)
        }
    }

    const handleSetPassword = (event: any) => {
        const { value } = event.target
        setPassword(value)

        try {
            (Yup.reach(validationSchema, 'password') as Yup.StringSchema).validateSync(value)
            const formErrorsNew = { ...formErrors }
            formErrorsNew.password = null
            setFormErrors(formErrorsNew)
        } catch (err) {
            const formErrorsNew = { ...formErrors }
            formErrorsNew.password = err
            setFormErrors(formErrorsNew)
        }
    }

    const handleSubmit = (event: any) => {
        event.preventDefault() // does not allow to refresh page on submit

        try {
            validationSchema.validateSync({ username, password }, { abortEarly: false })
            signInFunc(username, password)
        } catch (err) {
            const formErrorsNew = { ...formErrors }
            err.inner.forEach((error: Yup.ValidationError) => {
                if (error.path === 'username') {
                    formErrorsNew.username = error
                }
                if (error.path === 'password') {
                    formErrorsNew.password = error
                }
            })
            setFormErrors(formErrorsNew)
        }
    }

    return (
        <div className="app-login-page">
            <Form onSubmit={handleSubmit} data-testid="submitFormLogin">
                <div className="wrp">
                    <div className="authentication">
                        <h2>
                            <FormattedMessage id="Authentication" />
                        </h2>
                    </div>
                    <div className="login-controls">
                        <Form.Group controlId="userInput">
                            <Form.Label>
                                <FormattedMessage id="login.form.username" />
                            </Form.Label>
                            <Form.Control
                                autoFocus
                                name="username"
                                type="text"
                                value={username}
                                placeholder={usernamePlaceholder}
                                onChange={handleSetUsername}
                                isInvalid={!!formErrors.username}
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="login.form.username.invalid" />
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group controlId="pwdInput">
                            <Form.Label>
                                <FormattedMessage id="login.form.password" />
                            </Form.Label>
                            <Form.Control
                                type="password"
                                name="password"
                                value={password}
                                placeholder={passwordPlaceholder}
                                onChange={handleSetPassword}
                                isInvalid={!!formErrors.password}
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="login.form.password.invalid" />
                            </Form.Control.Feedback>
                        </Form.Group>
                        {canBeRestored && restoreLink}
                        <div className="wrp-btn">
                            <Button variant="primary" type="submit" className="btn-primary-login">
                                <FormattedMessage id="login.button" />
                            </Button>
                        </div>
                    </div>
                </div>
            </Form>
        </div>
    )
})
